/** @format */

import React, { ReactNode, useCallback, useEffect } from 'react';
import usePortal from 'react-cool-portal';
import { Modal } from './useGalleryModal.style';

const useGalleryModal = (options: ModalOptions = {}) => {
  const { Portal, ...rest } = usePortal({
    ...options,
    containerId: 'modal',
    defaultShow: false,
    internalShowHide: false,
  });

  const GalleryModal: React.FC<ModalProps> = useCallback(
    ({ isShow, children, onClose, background = '40, 40, 40', opacity = 0.6, transition = 0.15 }) => {
      const onCloseHandler = () => {
        onClose();
      };

      const escHandler = (e: React.KeyboardEvent<HTMLDivElement>) => {
        const { key } = e;
        if (key === 'Escape') {
          onClose();
        }
      };

      useEffect(() => {
        const getScrollbarWidth = () => {
          const div = document.createElement('div');
          div.style.width = '100px';
          div.style.height = '100px';
          div.style.overflow = 'scroll';
          div.style.position = 'absolute';
          div.style.top = '-9999px';

          document.body.appendChild(div);
          const scrollbarWidth = div.offsetWidth - div.clientWidth;
          document.body.removeChild(div);

          return scrollbarWidth;
        };

        const scrollbarWidth = getScrollbarWidth();
        const applyStylesToHtml = () => {
          if (isShow) {
            document.body.style.setProperty('padding-right', `${scrollbarWidth}px`);
            document.body.style.setProperty('overflow', 'hidden');
          } else {
            document.body.style.removeProperty('padding-right');
            document.body.style.removeProperty('overflow');
          }
        };

        applyStylesToHtml();
        window.addEventListener('keydown', escHandler);

        return () => {
          window.removeEventListener('keydown', escHandler);
          document.body.style.removeProperty('padding-right');
          document.body.style.removeProperty('overflow');
        };
      }, [isShow]);

      return (
        <Portal>
          <Modal
            $isShow={isShow}
            $background={background}
            $opacity={opacity}
            $transition={transition}
            onClick={onCloseHandler}
            tabIndex={-1}
            onKeyDown={escHandler}
            aria-hidden="true"
          >
            <div onClick={e => e.stopPropagation()}>{children}</div>
          </Modal>
        </Portal>
      );
    },
    [],
  );

  return { GalleryModal, ...rest };
};

export default useGalleryModal;

declare global {
  interface WindowEventMap {
    keydown: React.KeyboardEvent<HTMLInputElement>;
  }
}

interface ModalOptions {
  containerId?: string;
  defaultShow?: boolean;
  internalShowHide?: boolean;
  background?: string;
  opacity?: number;
  transition?: number;
}

interface ModalProps {
  isShow: boolean;
  children: ReactNode;
  onClose: () => void;
  background?: string;
  opacity?: number;
  transition?: number;
}
